import {
  Box,
  LinearProgress,
  linearProgressClasses,
  Pagination,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import isMobile from "is-mobile";
import { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import AssociateList from "../../components/AssociateList";
import { BOOZE_LIMITS, NARCS_LIMITS } from "../../constants/game";
import { RankXPName } from "../../constants/rank";
import { getRankListAction } from "../../reducers/profile.slice";
import { toUSDFormat } from "../../utils/utils";

import useStyles from "./index.styles";

const Associate = () => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();

  const { myProfile, profileNames, isLoadingRankList } = useAppSelector(
    (state) => state.profile
  );

  const rankIndex = myProfile.rankNumber ? myProfile.rankNumber - 1 : 0;
  const nextRankIndex =
    RankXPName.length > rankIndex + 1 ? rankIndex + 1 : rankIndex;

  const countPerPage = 15;
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(profileNames.length / countPerPage);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    handlePagination(value);
  };

  const handlePagination = (page: number) => {
    setPage(page);
  };

  const BorderLinearProgress = styled(LinearProgress)(
    ({ theme }: { theme: any }) => ({
      height: 10,
      borderRadius: 5,
      [`&.${linearProgressClasses.colorPrimary}`]: {
        background: theme.palette.grey[200],
        ...theme.applyStyles("dark", {
          background: theme.palette.grey[800],
        }),
      },
      [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        background: "linear-gradient(45deg,rgb(180, 96, 240),#5808fb)",
        ...theme.applyStyles("dark", {
          background: "linear-gradient(45deg, #9929ea,#5808fb)",
        }),
      },
    })
  );

  const HeaderDesktop = () => {
    return (
      <>
        <Box className={classes.headerLeft}>
          Your rank
          <Typography
            fontFamily={"Philosopher"}
            color={"white"}
            textAlign={"center"}
            fontSize={36}
            fontWeight={700}
          >
            {RankXPName[rankIndex]}
          </Typography>
          {BOOZE_LIMITS[rankIndex]} Booze units / {NARCS_LIMITS[rankIndex]}{" "}
          Narcotics units
        </Box>
        <Box className={classes.headerProgressBar}>
          <Box
            component={"img"}
            src="/assets/imgs/associate/medal.png"
            width={70}
          />
          <Box className={classes.progressText}>
            Rank progress {toUSDFormat(myProfile.rankPercent, 3)}%
          </Box>
          <Box className={classes.progress}>
            <BorderLinearProgress
              variant="determinate"
              value={myProfile.rankPercent || 0}
            />
          </Box>
          <Box className={classes.ranking}>#{myProfile.rankByRankXp}</Box>
        </Box>
        <Box className={classes.headerLeft}>
          <Box>Next rank</Box>
          <Typography
            fontFamily={"Philosopher"}
            color={"white"}
            textAlign={"center"}
            fontSize={36}
            fontWeight={700}
          >
            {RankXPName[nextRankIndex]}
          </Typography>
          <Box>
            {BOOZE_LIMITS[nextRankIndex]} Booze units /{" "}
            {NARCS_LIMITS[nextRankIndex]} Narcotics units
          </Box>
        </Box>
      </>
    );
  };

  const HeaderMobile = () => {
    return (
      <>
        <Box className={classes.headerLeftMobile}>
          <Box className={classes.headerLeft}>
            Your rank
            <Typography
              fontFamily={"Philosopher"}
              color={"white"}
              textAlign={"center"}
              fontSize={24}
              fontWeight={700}
            >
              {RankXPName[rankIndex]}
            </Typography>
            {BOOZE_LIMITS[rankIndex]} Booze units / {NARCS_LIMITS[rankIndex]}{" "}
            Narcotics units
          </Box>
          <Box className={classes.headerLeft}>
            <Box>Next rank</Box>
            <Typography
              fontFamily={"Philosopher"}
              color={"white"}
              textAlign={"center"}
              fontSize={24}
              fontWeight={700}
            >
              {RankXPName[nextRankIndex]}
            </Typography>
            <Box>
              {BOOZE_LIMITS[nextRankIndex]} Booze units /
              {NARCS_LIMITS[nextRankIndex]} Narcotics units
            </Box>
          </Box>
        </Box>
        <Box className={classes.headerProgressBar}>
          <Box
            component={"img"}
            src="/assets/imgs/associate/medal.png"
            width={70}
          />
          <Box className={classes.progressText}>
            Rank progress {toUSDFormat(myProfile.rankPercent, 3)}%
          </Box>
          <Box className={classes.progress}>
            <BorderLinearProgress
              variant="determinate"
              value={myProfile.rankPercent || 0}
            />
          </Box>
          <Box className={classes.ranking}>#{myProfile.rankByRankXp}</Box>
        </Box>
      </>
    );
  };

  useEffect(() => {
    dispatch(
      getRankListAction({
        from: (page - 1) * countPerPage,
        count: countPerPage,
      })
    );
  }, [countPerPage, dispatch, page]);

  return (
    <Box className={classes.body}>
      <Box className={classes.container}>
        <Box className={classes.header}>
          {isMobile() ? <HeaderMobile /> : <HeaderDesktop />}
        </Box>

        <AssociateList isLoadingRankList={isLoadingRankList} page={page} />

        <Stack spacing={2} alignItems="center" sx={{ mt: 3, mb: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            siblingCount={0}
            boundaryCount={1}
            onChange={handlePageChange}
            variant="outlined"
            shape="circular"
            sx={{
              "& .MuiPaginationItem-root": {
                color: "white",
                borderColor: "rgba(255, 255, 255, 0.23)",
                "&:hover": {
                  borderColor: "white",
                },
                "&.Mui-selected": {
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.12)",
                  },
                },
              },
            }}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default Associate;
